import {
  QueryClient,
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { isEqual, mapValues } from "lodash";
import { useMemo, useRef } from "react";
import {
  LOB,
  UpdateAddresses,
  UpdateReport,
  addComment,
  addManualAddress,
  deleteComment,
  editComment,
  calculatePremium,
  createCRMContact,
  deleteAdditionalFile,
  deleteEmailThread,
  fetchEmailThreads,
  getBatchComments,
  generatePolicyNumbers,
  getDeductibleSummary,
  getGoogleGroupStats,
  getIntegrationData,
  getLimitSummary,
  getUpdatingReports,
  listAdditionalFiles,
  listCRMContacts,
  listPolicyFlyApplications,
  listReport,
  listReports,
  listUsers,
  loadAddressesForMap,
  ratePrecheck,
  searchAddresses,
  loadAddresses,
  updateAddresses,
  updateCRMContact,
  updateReport,
  uploadAdditionalFile,
  uploadEmailThread,
} from "../../api/routes";
import * as client from "../../client";
import { useClientId } from "../../ClientConfig";
import {
  applyOverridesToAddress,
  applyOverridesToMetadataReponse,
} from "./Conversion";

const useReportsInner = (lobs: LOB[]) =>
  useQuery({
    enabled: lobs.length > 0,
    queryKey: ["reports", JSON.stringify(lobs)],
    queryFn: () => listReports(lobs),
    // Refetch every 2 minutes to avoid really stale data showing on the dashboard
    refetchInterval: 120_000,
  });

export const useReports = (lobs: LOB[]) => useReportsInner(lobs);
export const useSafeReports = (lobs: LOB[]) => {
  const clientId = useClientId();
  const reports = useReportsInner(lobs);
  return useMemo(
    () => ({
      ...reports,
      data: reports.data
        ? reports.data.map((report) =>
            applyOverridesToMetadataReponse(report, clientId)
          )
        : reports.data,
    }),
    [reports.data]
  );
};

export const usePolicyFlyApplications = (statuses: string) =>
  useQuery({
    queryKey: ["policyfly-applications", statuses],
    queryFn: () => listPolicyFlyApplications(statuses),
    refetchInterval: 120_000,
  });

export const useReport = (reportId?: string) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["reports", reportId],
    queryFn: async (): Promise<client.ReportResponse | undefined> => {
      if (!reportId) return undefined;

      const report = await listReport(reportId, false);

      const addressesBySov = await Promise.all(
        report.report_json.sovs.map(async (sov) => {
          const { addresses } = await loadAddresses(reportId, sov.sov_id);
          const live = addresses.filter((a) => !a.is_deleted);
          live.sort((a, b) =>
            (a.default_sort_key ?? "").localeCompare(b.default_sort_key ?? "")
          );
          return [sov.sov_id, live] as const;
        })
      );

      const bySovId = Object.fromEntries(addressesBySov);
      return {
        ...report,
        report_json: {
          ...report.report_json,
          sovs: report.report_json.sovs.map((sov) => ({
            ...sov,
            addresses: bySovId[sov.sov_id] ?? [],
          })),
        },
      };
    },
  });
};

export const useUpdateAddresses = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-addresses", reportId],
    mutationFn: async (update: UpdateAddresses) => {
      return await updateAddresses(reportId, update);
    },
    onSuccess: (data) => {
      // Invalidate report list data because updating addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: ["reports"] });

      const prev = queryClient.getQueryData<client.ReportResponse>([
        "reports",
        reportId,
      ]);

      const updates = Object.fromEntries(
        data.addresses.map((a) => [a.address_id, a])
      );
      if (prev) {
        queryClient.setQueryData<client.ReportResponse>(["reports", reportId], {
          ...prev,
          report_json: {
            ...prev.report_json,
            sovs: prev.report_json.sovs.map((sov) => ({
              ...sov,
              addresses: sov.addresses.map((a) => updates[a.address_id] ?? a),
            })),
          },
        });
      }
    },
  });
};

export const useAddManualAddress = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-manual-address", reportId],
    mutationFn: async (address: client.RawAddress) => {
      return await addManualAddress(reportId, address);
    },
    onSuccess: (data) => {
      // Invalidate report list data because adding addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: ["reports"] });

      queryClient.setQueryData(["reports", reportId], data.report);
    },
  });
};

export const useCalculatedPremium = (reportId: string, enabled: boolean) => {
  return useQuery({
    enabled,
    queryKey: ["reports", reportId, "calculated-premium"],
    queryFn: () => calculatePremium(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useRatePrecheck = (reportId: string) => {
  return useQuery({
    queryKey: ["reports", reportId, "rate-precheck"],
    queryFn: () => ratePrecheck(reportId),
  });
};

// Return type is intentionally "unknown" here so that callers don't use it
// directly, since it requires some interpretation. Instead, use useReport to
// read report data.
export const useUpdateReport = (
  reportId: string
): UseMutationResult<unknown, unknown, UpdateReport, unknown> => {
  const key = ["reports", reportId];
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: key,
    mutationFn: async (update: UpdateReport) => {
      return await updateReport(reportId, update);
    },
    onSuccess: (updateResult) => {
      const thisReportKey = ["reports", reportId];
      // invalidate prefix matches against queryKeys
      // Because the dashboard computes things like enabled TIV from the report,
      // conservatively assume that any change to the report could affect the
      // list view.
      queryClient.invalidateQueries({
        queryKey: ["reports"],
        // This call will trigger refetch immediately, so we have to be sure not
        // to invalidate the response data we will save below.
        predicate: (query) => !isEqual(query.queryKey, thisReportKey),
      });
      // Trigger a re-fetch of updating reports immediately
      queryClient.invalidateQueries({
        queryKey: ["updating-reports"],
      });

      const oldData =
        queryClient.getQueryData<client.ReportResponse>(thisReportKey);

      if (updateResult.use_old_sovs && oldData) {
        // Backend says to use old SOV data and we have data
        const newData = updateResult.report;
        queryClient.setQueryData<client.ReportResponse>(thisReportKey, {
          ...newData,
          report_json: {
            ...newData.report_json,
            sovs: oldData.report_json.sovs,
          },
        });
      } else if (!updateResult.use_old_sovs) {
        // Backend says to use new SOV data
        queryClient.setQueryData(thisReportKey, updateResult.report);
      } else {
        // Backend says to use old data but we don't have any. Shouldn't happen,
        // but in this case we fall back to a full reload.
        queryClient.invalidateQueries({ queryKey: thisReportKey });
      }
    },
  });
};

export const useUsers = () => {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => listUsers(),
    // Re-use the result for at least 5 seconds (just avoid re-fetching during load of the page, etc.)
    staleTime: 5000,
  });
};

export const useLocalUser = () => {
  return useUsers().data?.current_user;
};

export const useUserById = (
  userId: string | undefined | null
): client.User | undefined => {
  const users = useUsers();
  if (!userId) return undefined;
  return (users?.data?.all_company_users ?? []).find((u) => u.id === userId);
};

export const useCRMContacts = () => {
  return useQuery({
    queryKey: ["crm-contacts"],
    queryFn: () => listCRMContacts(),
    // Keep data fresh for 5 minutes before considering a refetch
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes even when unused
    gcTime: 10 * 60 * 1000,
    // Don't refetch when window regains focus
    refetchOnWindowFocus: false,
  });
};

/**
 * Merge a single contact into the cached `crm-contacts` list.
 * - Replaces an existing contact with the same id.
 * - Prepends the contact when it wasn't present (default behavior).
 * - Returns the new `ListContactsResponse` shape.
 */
function mergeContactIntoCache(
  queryClient: QueryClient,
  contact: client.ReportContact
): void {
  queryClient.setQueryData(
    ["crm-contacts"],
    (prev: client.ListContactsResponse | undefined) => {
      const existing = prev?.contacts ?? [];
      const replaced = existing.map((c) => (c.id === contact.id ? contact : c));
      const has = replaced.some((c) => c.id === contact.id);

      const contacts: client.ReportContact[] = has
        ? replaced
        : [contact, ...replaced];

      return { ...(prev ?? {}), contacts };
    }
  );
}
export const useCreateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-create-contact"],
    mutationFn: async (body: client.CreateContactRequest) => {
      return await createCRMContact(body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      }
    },
  });
};

export const useUpdateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-update-contact"],
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: client.UpdateContactRequest;
    }) => {
      return await updateCRMContact(id, body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      }
      // Other reports linking to the updated contact will need to be refreshed,
      // so invalidate the list of all reports.
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
};

export const useAddressSearch = (
  lobs: LOB[],
  query: string,
  terms: client.TermSearch[]
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["search-addresses", query, ...terms],
    queryFn: async () => {
      const response =
        query || terms.length > 0
          ? await searchAddresses(lobs, query, terms)
          : { by_report: {}, term_query: [] };

      return {
        by_report: mapValues(response.by_report, (addresses) =>
          addresses.map(applyOverridesToAddress)
        ),
      };
    },
  });
};

export const useAdditionalFiles = (
  reportId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["report", "additional-files", reportId],
    queryFn: () => listAdditionalFiles(reportId),
    enabled,
  });
};

export const useUploadAdditionalFiles = (
  reportId: string,
  onProgress: (e: AxiosProgressEvent) => void,
  isScreenshot: boolean
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["report", "upload-additional-file", reportId],
    mutationFn: (files: File[]) =>
      uploadAdditionalFile(reportId, files, onProgress, isScreenshot),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["reports", reportId] });
      queryClient.setQueryData(
        ["report", "additional-files", reportId],
        response
      );
    },
  });
};

export const useDeleteAdditionalFile = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["report", "delete-additional-file", reportId],
    mutationFn: (names: string[]) => deleteAdditionalFile(reportId, names),
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["report", "additional-files", reportId],
        response
      );
    },
  });
};

export const useAddressesForMap = (lobs: LOB[]) => {
  return useQuery({
    queryKey: ["addresses-for-map", lobs],
    queryFn: () => loadAddressesForMap(lobs),
    refetchOnWindowFocus: false,
    staleTime: 3_600_000,
  });
};

export const useComments = (reportIds: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["comments", JSON.stringify(reportIds)],
    queryFn: async () => {
      const data = await getBatchComments(reportIds);
      for (const [reportId, comments] of Object.entries(
        data.comments_by_report_id
      )) {
        queryClient.setQueryData(["comments", reportId], comments);
      }
      return data;
    },
    enabled: reportIds.length > 0,
    refetchOnWindowFocus: false,
  });
};

export const useCommentsForReport = (
  reportId: string,
  fetchEnabled: boolean = true
) => {
  return useQuery<client.Comments>({
    queryKey: ["comments", reportId],
    queryFn: async () => {
      const data = await getBatchComments([reportId]);
      return data.comments_by_report_id[reportId] ?? { all_comments: [] };
    },
    enabled: fetchEnabled,
    refetchOnWindowFocus: false,
  });
};

export const useAddComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["comments", reportId],
    mutationFn: async (message: string) => {
      return await addComment(reportId, message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["comments", reportId], data.comments);
    },
  });
};

export const useDeleteComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["comments", reportId],
    mutationFn: async (commentId: string) => {
      return await deleteComment(reportId, commentId);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["comments", reportId], data.comments);
    },
  });
};

export const useEditComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["comments", reportId],
    mutationFn: async ({
      commentId,
      message,
    }: {
      commentId: string;
      message: string;
    }) => {
      return await editComment(reportId, commentId, message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["comments", reportId], data.comments);
    },
  });
};

export const useUpdatingReports = () => {
  const queryClient = useQueryClient();
  const doneUpdates = useRef<Set<string>>(new Set());
  return useQuery({
    queryKey: ["updating-reports"],
    queryFn: async (): Promise<client.ListUpdatingReportsResponse> => {
      const response = await getUpdatingReports(
        new Date(performance.timeOrigin)
      );

      // If any report finishes, invalidate cached data for it.
      for (const update of response.updates) {
        if (update.outcome && !doneUpdates.current.has(update.update_id)) {
          doneUpdates.current.add(update.update_id);
          queryClient.invalidateQueries({
            queryKey: ["reports", update.report_id],
          });
          queryClient.invalidateQueries({
            queryKey: ["integration-data", update.report_id],
          });
        }
      }

      return response;
    },
    refetchInterval: 5_000,
    staleTime: 5_000,
  });
};

const INTEGRATION_TYPES_THAT_BLOCK_EDITS = ["RERUN_REPORT", "POLICY_FLY"];
export const useThisReportIsUpdating = (
  reportId: string,
  integrationType?: string
): { updating: client.ReportBeingUpdated } | undefined => {
  const updating = useUpdatingReports().data?.updates.find(
    (u) =>
      u.report_id === reportId &&
      !u.outcome &&
      INTEGRATION_TYPES_THAT_BLOCK_EDITS.includes(u.integration_type) &&
      (!integrationType || u.integration_type === integrationType)
  );
  return useMemo(() => updating && { updating }, [updating]);
};

export const useAllUpdatesForReport = (
  reportId: string,
  integrationType?: string
): client.ReportBeingUpdated[] => {
  const updates = useUpdatingReports().data?.updates ?? [];
  return useMemo(
    () =>
      updates.filter(
        (u) =>
          u.report_id === reportId &&
          !u.outcome &&
          (!integrationType || u.integration_type === integrationType)
      ),
    [updates, reportId, integrationType]
  );
};

export const useCancelJobs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["cancel-jobs"],
    mutationFn: async (updateIds: string[]) => {
      await Promise.all(
        updateIds.map((id) =>
          client.asgardCancelJobAsgardCancelJobPost({
            requestBody: { report_integration_id: id },
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["updating-reports"] });
      queryClient.invalidateQueries({ queryKey: ["queue-jobs"] });
    },
  });
};

export const useEmailThreads = (reportId: string, enabled: boolean = true) => {
  return useQuery({
    enabled: enabled,
    queryKey: ["email-threads", reportId],
    queryFn: () => fetchEmailThreads(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useUploadEmailThread = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["upload-email-thread", reportId],
    mutationFn: (files: File[]) => uploadEmailThread(reportId, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports", reportId] });
      queryClient.invalidateQueries({ queryKey: ["email-threads", reportId] });
    },
  });
};

export const useDeleteEmailThread = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-email-thread", reportId],
    mutationFn: (conversationId: string) =>
      deleteEmailThread(reportId, conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports", reportId] });
      queryClient.invalidateQueries({ queryKey: ["email-threads", reportId] });
      queryClient.invalidateQueries({
        queryKey: ["report", "additional-files", reportId],
      });
    },
  });
};

export const useGeneratePolicyNumbers = (reportId: string) => {
  return useMutation({
    mutationKey: ["generate-policy-numbers", reportId],
    mutationFn: async () => {
      return await generatePolicyNumbers(reportId);
    },
  });
};

export const useIntegrationData = (
  reportId: string,
  types: client.IntegrationDataRequest["types"]
) => {
  return useQuery({
    queryKey: ["integration-data", reportId, types],
    queryFn: () => getIntegrationData(reportId, types),
    refetchOnWindowFocus: false,
  });
};

export const useDeductibleSummary = (reportId: string) => {
  return useQuery({
    queryKey: ["reports", reportId, "deductible-summary"],
    queryFn: () => getDeductibleSummary(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useLimitSummary = (reportId: string) => {
  return useQuery({
    queryKey: ["reports", reportId, "limit-summary"],
    queryFn: () => getLimitSummary(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useGoogleGroupStats = (email: string | null) =>
  useQuery({
    enabled: !!email,
    queryKey: ["google-group-stats", email],
    queryFn: () => getGoogleGroupStats(email!),
    refetchOnWindowFocus: false,
  });
